/**
 * @fileoverview type annotation for content_optimization steps
 */

import type {
  ChannelOptimizationEventConfig,
  ContentOptimizationChannel,
  ContentOptimizationErrors,
  ContentOptimizationPhase,
} from "@lib/contentOptimization";

import type { MessageStepData } from "../MessageStep";
import type { SharedStepProps } from "../Steps.types";
import { eventToBehavior } from "./constants";

/**
 * The event type strings that map to TrackedUserBehavior classes on the backend.
 */
export type OptimizationEventType = "opens" | "clicks" | "reads" | "link_clicks";

/**
 * Conversion behavior class strings from the backend (TrackedUserBehavior).
 * Used at the serialization boundary when saving Canvas steps.
 */
export type ConversionBehaviorType = "TrackedUserBehavior::CanvasOpen" | "TrackedUserBehavior::CanvasClick";

export function toConversionBehavior(event: OptimizationEventType): ConversionBehaviorType {
  return eventToBehavior[event];
}

/**
 * Converts a conversion behavior type to the UI optimization event, using the
 * config's allowed list to disambiguate (e.g., CanvasOpen -> "opens" for email,
 * but "reads" for RCS).
 */
export function fromConversionBehavior(
  behavior: ConversionBehaviorType,
  config: ChannelOptimizationEventConfig
): OptimizationEventType {
  for (const event of config.allowed) {
    if (eventToBehavior[event] === behavior) {
      return event;
    }
  }
  return config.default;
}

export interface OptimizationConversionConfig {
  /** The optimization event type */
  event_type?: OptimizationEventType;
  /** The conversion window in seconds (default 24 hours = 86400) */
  performed_behavior_window_in_seconds: number;
  /** Braze link IDs (l_id) that count as click conversions when optimizing for clicks */
  conversion_links?: string[];
  /** Raw URL prefixes whose email or SMS clicks we ignore */
  excluded_link_prefixes?: string[];
}

/** Backend POST shape for optimization conversion config */
export interface OptimizationConversionConfigForPost {
  event_type: ConversionBehaviorType;
  performed_behavior_window_in_seconds: number;
  conversion_links?: string[];
  excluded_link_prefixes?: string[];
}

export interface SerializeOptimizationConversionConfigOptions {
  channel: ContentOptimizationChannel | null;
  excludedLinkPrefixesEnabled?: boolean;
}

/**
 * Container for all content optimization specific data
 */
export interface ContentOptimizerData {
  /** Component store data from backend */
  componentStoreData?: any;
  /** Number of combinations (optional, can be calculated) */
  combinationCount?: number;
  /** The optimization event type */
  optimizationEventType?: OptimizationEventType;
}

export interface ContentOptimizationStepData extends Omit<MessageStepData, "type"> {
  type: "content-optimization";
  /** Container for content optimizer specific props */
  contentOptimizerData?: ContentOptimizerData;
  validationErrors?: ContentOptimizationErrors;
  channel?: ContentOptimizationChannel;
  /** The optimization conversion configuration (event type and window) */
  optimizationConversionConfig?: OptimizationConversionConfig;
  /** The current optimization phase of the step */
  optimizationPhase?: ContentOptimizationPhase | null;
}

export interface ContentOptimizationStepStatsData {
  /** number of users that entered this step */
  entered: number;
  /** number of users that proceeded to the next step */
  proceeded: number;
  /** number of users who exited the Canvas during this step */
  exited: number;
}

export type ContentOptimizationStepProps = SharedStepProps<ContentOptimizationStepData>;
